import { create } from "zustand";

/**
 * Smart Offset Registry Store (v2)
 * 
 * Manages offset balances, local/imported offsets, credit aging,
 * and fraud risk calculations.
 */
export interface OffsetRegistryStoreState {
  offsetBalance: number;
  localOffsets: number;
  importedOffsets: number;
  creditAgeDays: number[];

  // Actions
  setOffsetBalance: (balance: number) => void;
  setLocalOffsets: (offsets: number) => void;
  setImportedOffsets: (offsets: number) => void;
  setCreditAgeDays: (ages: number[]) => void;

  // Derived outputs
  getFraudRisk: () => number;
  getLocalBoost: () => number;
  getAgingBuckets: () => {
    "0-30": number;
    "31-90": number;
    "91-365": number;
    "365+": number;
  };

  // Export
  exportOffsetBalance: () => void;
}

export const useOffsetRegistryStore = create<OffsetRegistryStoreState>((set, get) => ({
  offsetBalance: 0,
  localOffsets: 0,
  importedOffsets: 0,
  creditAgeDays: [],

  setOffsetBalance: (balance) => set({ offsetBalance: Math.max(0, balance) }),
  setLocalOffsets: (offsets) => set({ localOffsets: Math.max(0, offsets) }),
  setImportedOffsets: (offsets) => set({ importedOffsets: Math.max(0, offsets) }),
  setCreditAgeDays: (ages) => set({ creditAgeDays: ages }),

  getFraudRisk: () => {
    return get().importedOffsets * 0.04;
  },

  getLocalBoost: () => {
    return get().localOffsets * 0.02;
  },

  getAgingBuckets: () => {
    const ages = get().creditAgeDays;
    const buckets = {
      "0-30": 0,
      "31-90": 0,
      "91-365": 0,
      "365+": 0,
    };

    ages.forEach((age) => {
      if (age <= 30) buckets["0-30"]++;
      else if (age <= 90) buckets["31-90"]++;
      else if (age <= 365) buckets["91-365"]++;
      else buckets["365+"]++;
    });

    return buckets;
  },

  exportOffsetBalance: () => {
    const state = get();
    const report = {
      offsetBalance: state.offsetBalance,
      localOffsets: state.localOffsets,
      importedOffsets: state.importedOffsets,
      creditAgeDays: state.creditAgeDays,
      fraudRisk: state.getFraudRisk(),
      localBoost: state.getLocalBoost(),
      agingBuckets: state.getAgingBuckets(),
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "offsetBalance.json";
    a.click();
    URL.revokeObjectURL(url);
  },
}));











